import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { useApplicationContext } from "../../application/ApplicationContext";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { RoleType } from "../../models/Role";
import { DeclarationListItem } from "../../models/Declaration";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import {
  deleteOneDeclaration,
  getDeclarations,
} from "../../services/FinishingCenterService";
import "./DeclarationsPage.scss";
import { Utility } from "../../core/Utility";

export const DeclarationsPage = () => {
  const {
    //load and set global filters
    state,
    selectedDateFromSet,
    selectedDateToSet,
  } = useApplicationContext();

  const { t } = useTranslation();
  const toast = useRef(null);
  const navigate = useNavigate();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const today = new Date();
  const isCurrentBusinessYear = businessYear === today.getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  // inicijalni opseg – 30 dana unazad za tekuću godinu, ili cela poslovna godina za ostale
  const initialDateFrom =
    state.selectedDateFrom ??
    (isCurrentBusinessYear
      ? new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 30,
          0,
          0,
          1
        )
      : yearStart);
  const initialDateTo =
    state.selectedDateTo ?? (isCurrentBusinessYear ? today : yearEnd);

  const [dateFrom, setDateFrom] = useState<Date>(initialDateFrom);
  const [dateTo, setDateTo] = useState<Date>(initialDateTo);

  const [allDeclarations, setAllDeclarations] = useState<DeclarationListItem[]>(
    []
  );

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    declarationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    orderNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    batchCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    sort: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const resetFilters = () => {
    setFilters({
      declarationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      orderNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      batchCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
      productType: { value: null, matchMode: FilterMatchMode.CONTAINS },
      sort: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  useEffect(() => {
    setProcessing(true);
    const effectiveFrom = state?.selectedDateFrom
      ? new Date(state.selectedDateFrom)
      : initialDateFrom;

    const effectiveTo = state?.selectedDateTo
      ? new Date(state.selectedDateTo)
      : initialDateTo;

    getDeclarations(effectiveFrom, effectiveTo)
      .then((response: AxiosResponse) => {
        const allDeclarations = response.data.result;
        setAllDeclarations(allDeclarations);
        resetFilters();
      })
      .finally(() => setProcessing(false));
  }, [state.selectedDateFrom, state.selectedDateTo]);

  useEffect(() => {
    const today = new Date();
    const isCurrent = businessYear === today.getFullYear();
    const newFrom = isCurrent
      ? new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 30,
          0,
          0,
          1
        )
      : yearStart;
    const newTo = isCurrent ? today : yearEnd;
    setDateFrom(newFrom);
    setDateTo(newTo);
    selectedDateFromSet(newFrom);
    selectedDateToSet(newTo);
  }, [businessYear]);

  const onDateFromChange = (_property: string, value: Date | null) => {
    setDateFrom(value as Date);
    selectedDateFromSet(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
    selectedDateToSet(value as Date);
  };

  const accept = () => {
    setProcessing(true);
    deleteOneDeclaration(inputToDelete)
      .then(() => {
        setInputToDelete("");
      })
      .then(() => {
        const effectiveFrom = state?.selectedDateFrom
          ? new Date(state.selectedDateFrom)
          : initialDateFrom;

        const effectiveTo = state?.selectedDateTo
          ? new Date(state.selectedDateTo)
          : initialDateTo;

        getDeclarations(effectiveFrom, effectiveTo).then(
          (response: AxiosResponse) => {
            const allDeclarations = response.data.result;
            setAllDeclarations(allDeclarations);
            resetFilters();
          }
        );
      })
      .finally(() => setProcessing(false));
    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Deklaracija je izbrisana`),
      life: 3000,
    });
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const formatIssueDate = (rowData: DeclarationListItem) => {
    return Utility.formatDate(new Date(rowData?.issueDate as Date));
  };

  const formatValidUntil = (rowData: DeclarationListItem) => {
    return Utility.formatDate(new Date(rowData?.validUntil as Date));
  };

  const formatOptions = (rowData: DeclarationListItem) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/declarations/${id}`);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setInputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">{t("Deklaracije")}</span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const today = new Date();
                const isCurrent = businessYear === today.getFullYear();
                const newFrom = isCurrent
                  ? new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      today.getDate() - 30,
                      0,
                      0,
                      1
                    )
                  : yearStart;
                const newTo = isCurrent ? today : yearEnd;
                // lokalni state
                setDateFrom(newFrom);
                setDateTo(newTo);
                // globalni filteri
                selectedDateFromSet(newFrom);
                selectedDateToSet(newTo);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem",
              }}
              labelText={t("Nova deklaracije")}
              icon="bi bi-file"
              onButtonClick={() => {
                navigate(`/declarations/new`);
              }}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema podataka o deklaracijama"
              value={allDeclarations}
              showGridlines
              stripedRows
            >
              <Column
                field="declarationNumber"
                filter
                header={t("Broj deklaracije")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatIssueDate}
                header={t("Datum izdavanja")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatValidUntil}
                header={t("Važi do")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="orderNumber"
                filter
                header={t("Nalog za doradu")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="batchCode"
                filter
                header={t("Partija")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="productType"
                filter
                header={t("Biljna vrsta")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="sort"
                filter
                header={t("Sorta")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da izbrišete deklaraciju?")}
        header={t("Potvrda brisanja deklaracije")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
    </PageLayout>
  );
};
